/**
 * Vendas Page - Gerenciamento de Vendas
 */

import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { VendasTable } from "@/components/venda/VendasTable";
import { VendaSummaryCards } from "@/components/venda/VendaSummaryCards";
import { VendaFormDialog } from "@/components/venda/VendaFormDialog";
import { VendaViewDialog } from "@/components/venda/VendaViewDialog";
import { VendaDeleteDialog } from "@/components/venda/VendaDeleteDialog";
import { Venda } from "@/types/venda";
import { VendaFormData } from "@/schemas/vendaSchema";
import { vendasAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export default function Vendas() {
  const [vendas, setVendas] = useState<Venda[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedVenda, setSelectedVenda] = useState<Venda | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [actionLoading, setActionLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadVendas();
  }, []);

  const loadVendas = async () => {
    try {
      setLoading(true);
      const response = await vendasAdapter.list();
      setVendas(response.items);
    } catch (error) {
      console.error("Error loading vendas:", error);
      setVendas([]); // Garante UI consistente
      
      // Toast APENAS para erros REAIS (RLS, network, etc.)
      if (error && typeof error === 'object') {
        const errorMessage = (error as any)?.message || '';
        const errorCode = (error as any)?.code || '';
        
        // Ignora "banco vazio" ou resultados vazios normais
        if (
          !errorMessage.toLowerCase().includes('no rows') &&
          !errorMessage.toLowerCase().includes('empty') &&
          errorCode !== 'PGRST116' // Supabase: no rows
        ) {
          toast({
            title: "Erro ao carregar vendas",
            description: errorMessage || "Não foi possível carregar a lista de vendas.",
            variant: "destructive",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormMode("create");
    setSelectedVenda(null);
    setFormOpen(true);
  };

  const handleEdit = async (venda: Venda) => {
    try {
      setActionLoading(true);
      
      // Carregar venda completa com itens
      const vendaCompleta = await vendasAdapter.get(venda.id_venda!);
      
      if (!vendaCompleta) {
        toast({
          title: "Erro",
          description: "Venda não encontrada.",
          variant: "destructive",
        });
        return;
      }
      
      setFormMode("edit");
      setSelectedVenda(vendaCompleta);
      setFormOpen(true);
    } catch (error: any) {
      console.error("Erro ao carregar venda para edição:", error);
      toast({
        title: "Erro ao carregar",
        description: error?.message || "Não foi possível carregar os dados da venda.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleView = (venda: Venda) => {
    setSelectedVenda(venda);
    setViewOpen(true);
  };

  const handleDelete = (venda: Venda) => {
    setSelectedVenda(venda);
    setDeleteOpen(true);
  };

  const handleFormSubmit = async (data: VendaFormData) => {
    try {
      setActionLoading(true);

      if (formMode === "create") {
        await vendasAdapter.create(data);
        toast({
          title: "Venda criada",
          description: "A venda foi criada com sucesso.",
        });
      } else if (selectedVenda) {
        await vendasAdapter.update(selectedVenda.id_venda!, data);
        toast({
          title: "Venda atualizada",
          description: "A venda foi atualizada com sucesso.",
        });
      }

      loadVendas();
      setFormOpen(false);
    } catch (error: any) {
      console.error("Erro ao salvar venda:", error);
      
      // Detectar erros de autenticação/JWT
      if (
        error?.message?.toLowerCase().includes("jwt") || 
        error?.message?.toLowerCase().includes("auth") || 
        error?.message?.includes("não autenticado") ||
        error?.code === "PGRST301"
      ) {
        toast({
          title: "Sessão expirada",
          description: "Sua sessão expirou. A página será recarregada em 2 segundos.",
          variant: "destructive",
        });
        setTimeout(() => window.location.reload(), 2000);
        return;
      }
      
      toast({
        title: "Erro ao salvar",
        description: error?.message || "Não foi possível salvar a venda.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedVenda) return;

    try {
      setActionLoading(true);
      console.log("🗑️ Tentando deletar venda:", selectedVenda);
      await vendasAdapter.delete(selectedVenda.id_venda!);
      
      toast({
        title: "✅ Venda excluída",
        description: "A venda foi excluída com sucesso.",
      });

      loadVendas();
      setDeleteOpen(false);
    } catch (error: any) {
      console.error("❌ Erro ao deletar venda:", error);
      
      let errorMessage = "Não foi possível excluir a venda.";
      
      // Verificar se é erro de permissão RLS
      if (error?.message?.includes("policy") || error?.code === "42501") {
        errorMessage = "Você não tem permissão para excluir vendas. Apenas administradores podem realizar esta ação.";
      } else if (error?.message?.includes("foreign key") || error?.message?.includes("constraint")) {
        errorMessage = "A venda possui registros relacionados e não pode ser excluída.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao excluir",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Vendas</h1>
          <p className="text-muted-foreground">
            Gerencie as vendas do sistema
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Carregando vendas..." />
          </div>
        ) : (
          <>
            <VendaSummaryCards vendas={vendas} />
            <VendasTable
              vendas={vendas}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreate={handleCreate}
            />
          </>
        )}
      </div>

      <VendaFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        venda={selectedVenda}
        onSubmit={handleFormSubmit}
        isLoading={actionLoading}
      />

      <VendaViewDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        venda={selectedVenda}
      />

      <VendaDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        venda={selectedVenda}
        onConfirm={handleConfirmDelete}
        isLoading={actionLoading}
      />
    </Layout>
  );
}
